import React from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useSnackbar } from 'notistack';

import { DataAPIContext, CREATE } from '../api';
import { isBackgroundTaskResponse, notifyBackgroundTask } from '../bg_tasks/batchResponseHelper';
import { BackgroundTaskContext } from '../bg_tasks/BackgroundTaskProvider';
import LicenseeFormField from '../components/LicenseeFormField';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        maxWidth: 600,
    },
}));

const SplitOTABookingsPage = () => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const bgCtx = React.useContext(BackgroundTaskContext);
    const { isFranchise, currentLicensee } = React.useContext(ContextStore);

    const [selectedLicensee, setSelectedLicensee] = React.useState(currentLicensee || null);
    const effectiveTopId = isFranchise && selectedLicensee ? selectedLicensee : null;

    const [submitting, setSubmitting] = React.useState(false);
    const [userIds, setUserIds] = React.useState('');
    const [userEmails, setUserEmails] = React.useState('');
    const [dryRun, setDryRun] = React.useState(true);
    const [usePlimport] = React.useState(true);
    const [batchMultiplier] = React.useState(1);

    const hasInput = userIds.trim() || userEmails.trim();

    const handleSubmit = async () => {
        if (!hasInput) {
            enqueueSnackbar('Please enter at least one user ID or email', { variant: 'warning' });
            return;
        }

        setSubmitting(true);
        try {
            const params = {
                user_ids: userIds.trim()
                    ? userIds.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
                    : [],
                user_emails: userEmails.trim()
                    ? userEmails.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
                    : [],
                dry_run: dryRun,
                use_plimport: usePlimport,
                batch_multiplier: batchMultiplier,
                ...(effectiveTopId ? { tour_operator: effectiveTopId } : {}),
            };

            const res = await dataAPI(CREATE, 'bg_tasks', {
                urlSuffix: 'start_split_ota/',
                data: params,
            });

            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
            } else if (res && res.statusCode === 403) {
                enqueueSnackbar(res.data?.message || 'Permission denied', { variant: 'error' });
            } else if (res && res.statusCode >= 400) {
                enqueueSnackbar(res.data?.message || 'Error starting task', { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar('Error starting task', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={classes.root}>
            <PageMeta title="Split OTA Bookings" />
            <PageHeading sx={{ mb: 1 }}>Split OTA Bookings</PageHeading>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Delete bookings for specified users and reimport them from the original
                import data. Used when OTA assignments change and bookings need to be
                re-processed. Use dry run first to preview what will happen.
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <LicenseeFormField
                        value={selectedLicensee}
                        onChange={setSelectedLicensee}
                        error={isFranchise && !selectedLicensee}
                        helperText={isFranchise && !selectedLicensee ? 'Please select a licensee' : undefined}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="User IDs"
                        value={userIds}
                        onChange={(e) => setUserIds(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={8}
                        helperText="One per line or comma-separated"
                        placeholder={'123\n456\n789'}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="User Emails"
                        value={userEmails}
                        onChange={(e) => setUserEmails(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={8}
                        helperText="One per line or comma-separated"
                        placeholder={'user@example.com\nanother@example.com'}
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dryRun}
                                onChange={(e) => setDryRun(e.target.checked)}
                            />
                        }
                        label="Dry run (preview only, no changes)"
                    />
                </Grid>

                {/* <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={usePlimport}
                                onChange={(e) => setUsePlimport(e.target.checked)}
                                readOnly={true}
                                disabled={true}
                            />
                        }
                        label="Use parallel import"
                    />
                </Grid> */}

                {/* {usePlimport && (
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Batch multiplier"
                            type="number"
                            size="small"
                            variant="outlined"
                            value={batchMultiplier}
                            onChange={(e) => setBatchMultiplier(Math.min(8, Math.max(1, parseInt(e.target.value) || 1)))}
                            inputProps={{ min: 1, max: 8 }}
                            helperText="Parallel import batches"
                        />
                    </Grid>
                )} */}

                <Grid item xs={12} display="flex" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={2}>
                    {!dryRun && (
                        <Typography variant="caption" color="warning.main">
                            This will delete and reimport bookings. Use dry run first.
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color={dryRun ? 'primary' : 'warning'}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CallSplitIcon />}
                        onClick={handleSubmit}
                        disabled={submitting || !hasInput || (isFranchise && !selectedLicensee)}
                    >
                        {dryRun ? 'Preview (Dry Run)' : 'Split Bookings'}
                    </Button>
                </Grid>
            </Grid>
        </div>
    );
};

export default SplitOTABookingsPage;
